<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<div class="privacy-statement-container">
	<h1>Privacy Statement for Beyond NetZero: ICT Carbon Insights</h1>

	<section>
		<h2>Introduction</h2>
		<p>
			The NTT DATA, Inc. Group ('NTT DATA', 'us', 'we', 'our') understand the importance of
			ensuring that we collect, use, store, disclose, share, and dispose of (collectively
			'process' or 'processing') information about you ('personal data', including 'personal
			information' or 'personally identifiable information' as it may be defined under
			applicable data protection laws and regulations) in a transparent, fair, secure, and
			lawful way.
		</p>
		<p>
			This Privacy Statement describes our practices for processing personal data and your
			choices and rights in relation to personal data processed when you use NTT DATA's Beyond
			NetZero: ICT Carbon Insights ('ICT Carbon Insights').
		</p>
		<p>
			By using the ICT Carbon Insights, you agree to the practices described in this Privacy
			Statement.
		</p>
		<p>
			For purposes of this Privacy Statement, the NTT DATA, Inc. Group means NTT DATA, Inc and
			its subsidiaries.
		</p>
	</section>

	<section>
		<h2>About ICT Carbon Insights</h2>
		<p>
			The ICT Carbon Insights is a digital platform that provides a comprehensive assessment
			of an organization's IT estate. It delivers service-level, activity-based emissions
			calculations across data centres, networks, devices, cloud services, and applications.
		</p>
	</section>

	<section>
		<h2>Personal data that we collect about you</h2>
		<p>When you use the ICT Carbon Insights, your personal data may be processed.</p>
		<h3>Personal data that we collect directly from you</h3>
		<ul>
			<li>Your first name, last name, work phone number and work email address.</li>
			<li>Your username and password to access the ICT Carbon Insights.</li>
			<li>Personal data contained in any tickets submitted by you.</li>
			<li>Personal data contained in any communications with you.</li>
			<li>
				Personal data contained in any feedback, ratings or preferences collected from you
				on your satisfaction and engagement with the ICT Carbon Insights.
			</li>
			<li>Personal data contained in any referral programmes you participate in.</li>
		</ul>
		<h3>Personal data that we automatically collect</h3>
		<p>
			Anonymous information, such as time using the ICT Carbon Insights. We do this in order
			to understand what the ICT Carbon Insights engagement is like.
		</p>
		<p>
			We may collect certain information by automated means, such as cookies and web beacons,
			as described in more detail below. The information we may collect by automated means
			includes, but is not limited to:
		</p>
		<ul>
			<li>
				Information about the devices used to access the ICT Carbon Insights (such as the IP
				address and the device, browser and operating system type).
			</li>
			<li>Dates and times of visits to the ICT Carbon Insights.</li>
			<li>
				Information on actions taken on the ICT Carbon Insights (such as page views, site
				navigation patterns or application activity).
			</li>
			<li>
				A general geographic location (such as country and city) from which you access the
				ICT Carbon Insights.
			</li>
			<li>Search terms that you use to reach the ICT Carbon Insights.</li>
		</ul>
		<h3>Personal data that we collect from other sources</h3>
		<ul>
			<li>
				We may access information about you from the company you work for when we provide
				the ICT Carbon Insights as specified in our agreed contract with you or the company
				you work for.
			</li>
			<li>
				We may collect information about you from our subsidiaries and affiliated entities.
			</li>
		</ul>
		<p>
			We do not process sensitive personal data about you, e.g. information relating to your
			health, biometric or genetic information, religion, political beliefs, union membership,
			race or sexual orientation and ask that you do not submit or provide this information to
			us when using the ICT Carbon Insights. We do not knowingly process information about
			children without appropriate parental or guardian consent and ask that you do not submit
			or provide this information to us when using the ICT Carbon Insights.
		</p>
	</section>

	<section>
		<h2>How we process your personal data</h2>
		<p>We process your personal data for the following purposes:</p>
		<ul>
			<li>
				<strong>Performance of contract:</strong> To provide the ICT Carbon Insights as
				specified in our contract, including provisioning of the ICT Carbon Insights and
				enabling the use of certain features.
			</li>
			<li>
				<strong>User account management:</strong> To authenticate you and to provide you
				access to the ICT Carbon Insights.
			</li>
			<li>
				<strong>Ticket resolution:</strong> To communicate and co-ordinate with you in a
				timely manner to assist in resolution of support requests or support tickets logged
				with us.
			</li>
			<li>
				<strong>Communications:</strong> To send communications to you related to the ICT
				Carbon Insights.
			</li>
			<li>
				<strong>Reporting on contract performance:</strong> To report on services provided
				to you and resolution activities.
			</li>
			<li>
				<strong>Business process improvements:</strong> To collect your feedback, ratings,
				and preferences on the ICT Carbon Insights, to evaluate your satisfaction and
				engagement with the ICT Carbon Insights and to improve the ICT Carbon Insights.
			</li>
			<li>
				<strong>Performance, statistics and traffic patterns:</strong> To compile
				statistical data about the ICT Carbon Insights, analyse the performance and
				usability of the ICT Carbon Insights, measure traffic patterns for the ICT Carbon
				Calculator and determine which areas of the ICT Carbon Insights have been visited.
			</li>
		</ul>
	</section>

	<section>
		<h2>How we use automated decision-making</h2>
		<p>
			When using the ICT Carbon Insights, no automated decisions, including profiling, will be
			made that have any legal or significant effects on you.
		</p>
	</section>

	<section>
		<h2>Artificial intelligence</h2>
		<p>
			We may enable certain features on the ICT Carbon Insights which includes the use of
			artificial intelligence ('AI') technologies. including automation, natural language
			processing, machine learning, and generative AI. We are committed to responsible AI
			practices that prioritize transparency, fairness, and data privacy.
		</p>
	</section>

	<section>
		<h2>Our legal basis for processing your personal data</h2>
		<p>When we process your personal data, we rely on the following legal basis:</p>
		<p>
			<strong>Performance of contract:</strong> To provide the services specified in our
			contract.
		</p>
	</section>

	<section>
		<h2>How we disclose your personal data</h2>
		<p>We may disclose your personal data to the following parties:</p>
		<ul>
			<li>
				<strong>Our subsidiaries, affiliates and other NTT DATA companies:</strong> We may
				disclose information about you with our subsidiaries and affiliated entities for the
				purposes of operating our business, delivering, analyzing, improving, securing, and
				customizing the ICT Carbon Insights, and for other legitimate purposes permitted by
				applicable law or otherwise with your consent.
			</li>
			<li>
				<strong>Authorized personnel and advisors:</strong> We may disclose information
				about you with authorized NTT DATA personnel, agents and professional advisors to
				support business operations and activities.
			</li>
			<li>
				<strong>Authorized service providers and third parties:</strong> We may disclose
				information about you with our service providers or other third parties for service
				delivery purposes.
			</li>
		</ul>
		<p>
			We do not sell or share personal data of employees as defined by the California Privacy
			Rights Act.
		</p>
		<p>
			Any service providers, third parties or other parties with whom we disclose personal
			data are contractually not permitted to use personal data for any purpose other than the
			purpose for which the personal data is provided or given access to.
		</p>
	</section>

	<section>
		<h2>Cross-border transfers</h2>
		<p>
			As a global company, we may transfer your personal data to countries where we do
			business or to international organizations in connection with the purposes identified
			above and in accordance with this Privacy Statement.
		</p>
		<p>
			When we transfer your personal data from the country from where it was originally
			collected to another country, we take steps to ensure that appropriate safeguards are in
			place, in line with applicable law, to protect your personal data. You may obtain a copy
			of these safeguards by contacting us in accordance with the 'How to contact us' section
			below.
		</p>
	</section>

	<section>
		<h2>Security of your personal data</h2>
		<p>
			The security of your personal data is very important to us. We are committed to
			protecting your personal data from accidental or unlawful destruction, loss, alteration,
			unauthorized access or disclosure by using a combination of physical, administrative and
			technical safeguards ourselves and contractually requiring third parties, service
			providers, and other external parties to whom we disclose your personal data to do the
			same.
		</p>
		<p>
			The safety and security of your personal data also depends on you. You are responsible
			for keeping your username and password that you use to access the ICT Carbon Insights
			confidential.
		</p>
	</section>

	<section>
		<h2>Your privacy rights</h2>
		<p>
			You have the right to obtain information about and access the personal data that we hold
			about you, and the right to request the rectification, erasure, restriction of
			processing or portability of that personal data. Furthermore, you have the right to
			object to processing of your personal data and withdraw consent. You further have the
			right to make a complaint or raise a concern about how we process your personal data and
			the right not to be discriminated against for exercising your privacy rights.
		</p>
		<p>
			You may submit a request to enforce your rights at any time. You can make a request
			regarding your privacy rights by contacting us via one of the methods provided in the
			'How to contact us' section of this Privacy Statement.
		</p>
		<p>
			When you submit a request to us, you must provide sufficient information to allow us to
			verify that you are the person about whom the personal data relates. This information
			must contain sufficient detail to allow us to properly understand, evaluate and respond
			to your request. If we cannot verify your identity, we will not be able to respond to
			your request. Once we receive your request, we will begin the process to verify that you
			are the person that is the subject of the request. This may include matching identifying
			information provided by you with the information we have about you in our records.
		</p>
		<p>
			Where permitted under applicable law, we may charge you a reasonable fee to access your
			personal data; however, we will advise you of any fee in advance.
		</p>
		<p>
			We will respond to all requests and complaints in accordance with our policies and will
			acknowledge any requests within 10 days and respond substantively within 30 days.
		</p>
		<p>
			In some situations, we may not be required to enforce these rights, where permitted
			under applicable law and where exceptions may apply. We may also have a legitimate
			business interest to decline your request to action your rights.
		</p>
		<p>
			Individuals may hold additional rights under any local laws and regulations in certain
			countries, which we will recognize and uphold.
		</p>
	</section>

	<section>
		<h2>Authorized agent information</h2>
		<p>
			You may designate an authorized agent to make a request on your behalf. When your
			authorized agent makes a request related to your Personal Data, we will require the
			agent to provide written permission from you. We may also require that you verify your
			own identity directly with us at the time such a request is made.
		</p>
		<p>
			In order to allow an authorized agent to make a request on your behalf, you may contact
			us via one of the methods provided in the 'How to contact us' section of this Privacy
			Statement.
		</p>
	</section>

	<section>
		<h2>Links to third party websites and applications</h2>
		<p>
			We may provide links to the websites of other parties. We are not responsible for and
			make no representations or warranties in relation to the security, privacy practices and
			content of these websites. Please ensure that you read the applicable privacy and
			cookies policies before sharing personal data on these sites. Your use of such websites
			is at your own risk.
		</p>
	</section>

	<section>
		<h2>Retaining your personal data</h2>
		<p>
			We retain your personal data for as long as is reasonably necessary and proportionate to
			fulfil the purpose for which it was collected, processed, or for another disclosed
			purpose, unless a longer retention period is required to comply with legal obligations,
			resolve disputes, protect our assets, or enforce our rights.
		</p>
		<p>
			We will retain correspondence, documents and information related to any requests or
			complaints regarding your privacy rights for a period of 24 months or as required by any
			applicable laws and regulations.
		</p>
		<p>
			In some instances, we may anonymize your information in accordance with our policies and
			may keep those anonymized records for longer periods.
		</p>
	</section>

	<section>
		<h2>Changes or updates to this Privacy Statement</h2>
		<p>
			We may update this Privacy Statement at any time for any reason. If we do, we will
			update the 'Version' and 'Date'. All material changes will be communicated to you. We
			encourage you to regularly review this Privacy Statement to stay informed about how we
			use your personal data.
		</p>
	</section>

	<section>
		<h2>How to contact us</h2>
		<p>
			If you have any questions about how we use your personal data, have a privacy concern,
			or wish to make a request or complaint relating to your personal data, contact us via
			one of the following:
		</p>
		<ul>
			<li>
				<strong>Webform:</strong>
				<a
					href="https://privacyportal-de.onetrust.com/webform/a563fd46-b66a-43b0-b714-35e41095dc7a/217418ff-4692-4613-8ee4-7f7effd0b986"
					target="_blank"
					rel="noopener noreferrer"
					>Click here to access our privacy rights webform</a
				>
			</li>
			<li>
				<strong>Email Address:</strong>
				<a href="mailto:privacyoffice@nttdata.com">privacyoffice&#64;nttdata.com</a>
			</li>
			<li><strong>Phone Number (US):</strong> 1-888-914-9661 Access code: 111342</li>
		</ul>
	</section>
</div>
